import React, { useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import { getTutorialForPath } from './tutorialData';

// Create styles outside component to avoid recreation on each render
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  modal: {
    backgroundColor: '#FFFAFA',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '80vh',
    overflow: 'auto',
    position: 'relative',
    padding: '20px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    borderBottom: '1px solid #D2BA7B',
    paddingBottom: '10px'
  },
  title: {
    color: '#243444',
    margin: 0,
    fontSize: '24px',
    fontWeight: 'bold'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#4C5454'
  },
  content: {
    marginBottom: '30px',
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#4C5454'
  },

  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '20px',
    borderTop: '1px solid #D2BA7B',
    paddingTop: '20px'
  },
  progressIndicator: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px'
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#D2BA7B',
    margin: '0 5px',
    opacity: 0.5
  },
  activeDot: {
    opacity: 1,
    backgroundColor: '#243444'
  },
  button: {
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    border: 'none'
  },
  primaryButton: {
    backgroundColor: '#243444',
    color: '#D2BA7B'
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    color: '#4C5454',
    border: '1px solid #4C5454'
  },
  hidden: {
    display: 'none'
  }
};

// Add CSS for tutorial highlight
const addTutorialStyles = () => {
  if (!document.getElementById('tutorial-highlight-style')) {
    const style = document.createElement('style');
    style.id = 'tutorial-highlight-style';
    style.innerHTML = `
      .tutorial-highlight {
        box-shadow: 0 0 0 4px #D2BA7B, 0 0 0 8px rgba(210, 186, 123, 0.3);
        position: relative;
        z-index: 1001;
        transition: box-shadow 0.3s ease;
      }
    `;
    document.head.appendChild(style);
  }
};

const AppTutorial = () => {
  const { showTutorial, setShowTutorial, tutorialStep, setTutorialStep, completeTutorial } = useContext(AuthContext);
  const location = useLocation();
  const [tutorialSteps, setTutorialSteps] = useState([]);

  // Add tutorial styles on mount
  useEffect(() => {
    addTutorialStyles();
    
    return () => {
      // Remove any highlight classes when component unmounts
      document.querySelectorAll('.tutorial-highlight').forEach(el => {
        el.classList.remove('tutorial-highlight');
      });
    };
  }, []);
  
  // Update tutorial steps when path changes or tutorial is shown
  useEffect(() => {
    if (showTutorial) {
      const currentPathTutorial = getTutorialForPath(location.pathname);
      setTutorialSteps(currentPathTutorial);
    }
  }, [showTutorial, location.pathname]);

  // Highlight target element if specified
  useEffect(() => {
    if (showTutorial && tutorialSteps.length > 0 && tutorialStep < tutorialSteps.length) {
      const currentStep = tutorialSteps[tutorialStep];
      if (currentStep && currentStep.targetElement) {
        const targetElement = document.querySelector(`[data-testid="${currentStep.targetElement}"]`) || 
                             document.getElementById(currentStep.targetElement);
        
        if (targetElement) {
          // Add highlight effect
          targetElement.classList.add('tutorial-highlight');
          
          // Scroll element into view if needed
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Return cleanup function
          return () => {
            targetElement.classList.remove('tutorial-highlight');
          };
        }
      }
    }
  }, [showTutorial, tutorialStep, tutorialSteps]);

  const handleNext = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (tutorialStep > 0) {
      setTutorialStep(tutorialStep - 1);
    }
  };

  const handleComplete = () => {
    completeTutorial();
  };

  const handleSkip = () => {
    completeTutorial();
  };

  // Don't render anything if tutorial is not shown or no steps
  if (!showTutorial || tutorialSteps.length === 0 || tutorialStep >= tutorialSteps.length) {
    return null;
  }

  const currentStep = tutorialSteps[tutorialStep];

  return (
    <div style={{...styles.overlay, display: showTutorial ? 'flex' : 'none'}}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>{currentStep.title}</h2>
          <button style={styles.closeButton} onClick={handleSkip}>×</button>
        </div>
        
        <div style={styles.content}>
          <p>{currentStep.content}</p>
        </div>
        
        <div style={styles.progressIndicator}>
          {tutorialSteps.map((_, index) => (
            <div 
              key={index} 
              style={{
                ...styles.dot,
                ...(index === tutorialStep ? styles.activeDot : {})
              }}
            />
          ))}
        </div>
        
        <div style={styles.footer}>
          <div>
            {tutorialStep > 0 && (
              <button 
                style={{...styles.button, ...styles.secondaryButton}} 
                onClick={handlePrevious}
              >
                Previous
              </button>
            )}
          </div>
          
          <div>
            <button 
              style={{...styles.button, ...styles.secondaryButton, marginRight: '10px'}} 
              onClick={handleSkip}
            >
              Skip Tutorial
            </button>
            
            <button 
              style={{...styles.button, ...styles.primaryButton}} 
              onClick={handleNext}
            >
              {tutorialStep < tutorialSteps.length - 1 ? 'Next' : 'Finish'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppTutorial;
